import { theme } from '@arnold/common';
import styled from '@emotion/styled';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  h1 {
    font-size: 26px;
    margin: 0;
  }
  h2 {
    font-size: 17px;
    margin: 0;
  }
  h3 {
    font-size: 14px;
    margin: 0;
  }
`;
export const Panel = styled.section`
  min-width: 0;
  padding: 20px;
  border: 1px solid ${theme.colors.borderSeparator.default};
  border-radius: 12px;
  background: ${theme.colors.backgroundCover.default};
`;
export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;
export const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  min-width: 0;
`;
export const PanelHeader = styled(Bar)`
  justify-content: space-between;
  margin-bottom: 16px;
`;
export const Hint = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: ${theme.colors.text.secondary};
  margin: 0;
  overflow-wrap: anywhere;
`;
export const Badge = styled.span`
  font-size: 11px;
  border-radius: 20px;
  padding: 3px 8px;
  background: ${theme.colors.backgroundBasic.active};
  color: ${theme.colors.text.secondary};
`;
export const Action = styled.button<{ primary?: boolean }>`
  border: 1px solid ${({ primary }) => (primary ? theme.colors.brand.primary : theme.colors.borderSeparator.default)};
  background: ${({ primary }) => (primary ? theme.colors.brand.primary : theme.colors.backgroundCover.default)};
  color: ${({ primary }) => (primary ? theme.colors.textInverted.primary : theme.colors.text.primary)};
  padding: 8px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  &:hover:not(:disabled) {
    box-shadow: 0 0 0 1px ${theme.colors.brand.primary};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;
export const IconAction = styled(Action)`
  padding: 5px 8px;
  min-width: 28px;
`;
export const Segments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  padding: 3px;
  border-radius: 9px;
  background: ${theme.colors.backgroundBasic.active};
`;
export const Segment = styled.button<{ active: boolean }>`
  padding: 7px 11px;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: ${({ active }) => (active ? 500 : 400)};
  color: ${({ active }) => (active ? theme.colors.brand.primary : theme.colors.text.secondary)};
  background: ${({ active }) => (active ? theme.colors.backgroundCover.default : 'transparent')};
`;
export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  input,
  select,
  textarea {
    width: 100%;
    box-sizing: border-box;
    min-width: 0;
    border-radius: 7px;
    padding: 9px 10px;
    border: 1px solid ${theme.colors.borderSeparator.default};
    color: ${theme.colors.text.primary};
    background: ${theme.colors.backgroundCover.default};
    font-size: 13px;
  }
`;
export const Check = styled.label`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  margin: 0;
  overflow-wrap: anywhere;
  input {
    flex-shrink: 0;
    margin-top: 3px;
  }
`;
export const Disclosure = styled.details`
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  padding-top: 12px;
  > summary {
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: ${theme.colors.text.secondary};
  }
  > div {
    margin-top: 14px;
  }
`;
export const OptionsGrid = styled.div`
  display: grid;
  align-items: start;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: 20px;
`;
export const CheckList = styled(Stack)`
  max-height: 220px;
  overflow: auto;
  padding: 4px;
`;
export const Workspace = styled.div<{ open: boolean }>`
  display: grid;
  grid-template-columns: ${({ open }) => (open ? 'minmax(0, 1fr) minmax(300px, 380px)' : 'minmax(0, 1fr)')};
  gap: 20px;
  align-items: start;
  @media (max-width: 1400px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
export const ColumnList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 8px;
`;
export const ColumnCard = styled.div<{ selected: boolean }>`
  min-width: 0;
  padding: 12px;
  border-radius: 9px;
  border: 1px solid ${({ selected }) => (selected ? theme.colors.brand.primary : theme.colors.borderSeparator.default)};
  background: ${theme.colors.backgroundCover.default};
`;
export const ColumnTitle = styled.strong`
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;
export const ColumnBinding = styled(Hint)`
  margin: 7px 0 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 36px;
`;
export const Composer = styled(Stack)`
  padding: 18px;
  border: 1px solid ${theme.colors.brand.primary};
  border-radius: 10px;
  background: ${theme.colors.backgroundCover.default};
`;
export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;
export const Tile = styled.button<{ active?: boolean }>`
  text-align: left;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;
  border: 1px solid ${({ active }) => (active ? theme.colors.brand.primary : theme.colors.borderSeparator.default)};
  background: ${({ active }) => (active ? theme.colors.backgroundBasic.active : theme.colors.backgroundCover.default)};
  color: ${theme.colors.text.primary};
  font-size: 12px;
  strong {
    display: block;
    margin-bottom: 4px;
    font-weight: 500;
  }
  span {
    display: block;
    font-size: 11px;
    line-height: 1.4;
    color: ${theme.colors.text.secondary};
  }
`;
export const NumberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;
export const PropertiesPanel = styled(Panel)`
  padding: 16px 20px;
`;
export const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: 14px 24px;
  align-items: start;
`;
export const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
export const PresetButton = styled(Tile)`
  padding: 16px;
  strong {
    font-size: 14px;
    margin-bottom: 8px;
  }
  span {
    font-size: 12px;
  }
`;
